import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { X, Copy, Share2, Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// PatientSignUpLinkDialog
//
// Quick front-desk access to the clinic's reusable self-registration
// link: a big scannable QR plus Copy / Share. Reuses the same RPC +
// token as Settings → Patient Sign-Up — no separate source of truth.
// =====================================================================

export default function PatientSignUpLinkDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  const publicUrl = settings?.token ? `${window.location.origin}/r/${settings.token}` : "";

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    supabase.rpc("get_self_registration_settings").then(({ data, error: err }) => {
      setLoading(false);
      if (err) { setError(err.message || "Could not load the sign-up link."); return; }
      setSettings(data);
    });
  }, [open]);

  const close = () => onOpenChange(false);

  function copyLink() {
    if (!publicUrl) return;
    navigator.clipboard?.writeText(publicUrl);
    toast.success("Link copied.");
  }

  async function shareLink() {
    if (!publicUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Register as a new patient", url: publicUrl });
      } catch (e) {
        // User dismissed the share sheet — nothing to do.
      }
    } else {
      copyLink();
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="signup-link-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Patient Sign-Up</h2>
            <p className="text-xs text-slate-500 mt-0.5">Have a new patient scan this to register themselves.</p>
          </div>
          <button type="button" onClick={close} data-testid="signup-link-close" className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-teal-500" /></div>
          ) : error ? (
            <div className="py-6 text-center text-sm text-rose-600" data-testid="signup-link-error">{error}</div>
          ) : (
            <>
              {settings?.enabled === false ? (
                <div className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="signup-link-off-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    Online sign-up is currently <strong>off</strong> — anyone who scans will see a “closed” message. Turn it on in{" "}
                    <Link to="/settings/self-registration" onClick={close} className="underline font-medium">Settings → Patient Sign-Up</Link>.
                  </span>
                </div>
              ) : null}

              <div className="flex flex-col items-center gap-3">
                <div className="p-3 bg-white rounded-xl border border-slate-100" data-testid="signup-link-qr">
                  <QRCodeCanvas value={publicUrl || "https://dental-os.app"} size={220} includeMargin level="M" />
                </div>
                <code className="text-xs break-all text-center text-slate-600" data-testid="signup-link-url">{publicUrl}</code>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  type="button" onClick={copyLink} data-testid="signup-link-copy"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  <Copy className="w-4 h-4" /> Copy link
                </button>
                <button
                  type="button" onClick={shareLink} data-testid="signup-link-share"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700"
                >
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
